import axios from "axios";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectBodyshop } from "../redux/user/user.selectors";

const FeatureFlagContext = createContext({
  config: {},
  factory: null,
  flags: {},
  isReady: true,
  source: "local"
});

const OFF_TREATMENT = Object.freeze({ treatment: "off", config: null });
const LOCAL_STORAGE_PREFIX = "bodyshop-feature-flags";
const LOCAL_STORAGE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const FEATURE_FLAGS_REFRESH_DEBOUNCE_MS = 150;
const MAX_SCHEDULE_REFRESH_DELAY_MS = 2_147_483_647;
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const hasSchedule = (value) => value.activeDate != null || value.deactiveDate != null;

export const FEATURE_FLAGS_CHANGED_EVENT = "feature-flags-changed";

/**
 * Parses optional schedule timestamps into comparable epoch milliseconds.
 */
const parseDate = (value) => {
  if (value == null || value === "") return null;
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? null : timestamp;
};

/**
 * Determines whether a scheduled feature flag assignment is active at the current time.
 */
const isWithinSchedule = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return true;

  const now = Date.now();
  const startsAt = parseDate(value.activeDate);
  const endsAt = parseDate(value.deactiveDate);

  if (startsAt != null && now < startsAt) return false;
  if (endsAt != null && now >= endsAt) return false;

  return true;
};

/**
 * Normalizes backend config values into the object/string/null shape Split hooks expect.
 */
const normalizeConfig = (config) => {
  if (config == null || config === "") return null;
  if (typeof config === "string") {
    try {
      return JSON.parse(config);
    } catch {
      return config;
    }
  }
  return config;
};

/**
 * Converts legacy boolean-ish values and custom treatment strings into a stable treatment value.
 */
const normalizeTreatment = (value) => {
  if (typeof value === "boolean") return value ? "on" : "off";
  if (typeof value === "number") return value > 0 ? "on" : "off";

  if (typeof value === "string") {
    const normalized = value.trim();
    const lowered = normalized.toLowerCase();

    if (lowered === "true") return "on";
    if (lowered === "false") return "off";
    if (lowered === "on" || lowered === "off" || lowered === "control") return lowered;

    const dateValue = Date.parse(normalized);
    if (!Number.isNaN(dateValue)) return dateValue > Date.now() ? "on" : "off";

    return normalized;
  }

  return value ? "on" : "off";
};

/**
 * Converts any supported backend flag value into a Split-compatible treatment/config pair.
 */
const normalizeFlagValue = (value) => {
  if (value == null) return OFF_TREATMENT;

  if (typeof value === "object" && !Array.isArray(value)) {
    if (!isWithinSchedule(value)) return OFF_TREATMENT;

    if (hasOwn(value, "treatment")) {
      return {
        treatment: normalizeTreatment(value.treatment),
        config: normalizeConfig(value.config)
      };
    }

    if (hasOwn(value, "enabled")) {
      return {
        treatment: normalizeTreatment(value.enabled),
        config: normalizeConfig(value.config)
      };
    }

    if (hasSchedule(value)) {
      return {
        treatment: "on",
        config: normalizeConfig(value.config)
      };
    }
  }

  return {
    treatment: normalizeTreatment(value),
    config: null
  };
};

/**
 * Checks whether a socket/browser feature-flag change event applies to the active bodyshop.
 */
const isFeatureFlagChangeRelevant = (detail, bodyshopId) => {
  if (!detail || detail.scope === "global") return true;
  if (!bodyshopId) return false;
  return String(detail.bodyshopId) === String(bodyshopId);
};

/**
 * Finds the next scheduled flag boundary that should force a local re-render.
 */
const getNextScheduleRefreshDelay = (flags = {}, now = Date.now()) => {
  const nextTimestamp = Object.values(flags).reduce((next, value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return next;

    const timestamps = [parseDate(value.activeDate), parseDate(value.deactiveDate)].filter(
      (timestamp) => timestamp != null && timestamp > now
    );
    if (!timestamps.length) return next;

    const candidate = Math.min(...timestamps);
    return next == null ? candidate : Math.min(next, candidate);
  }, null);

  if (nextTimestamp == null) return null;

  return Math.min(Math.max(nextTimestamp - now + 50, 0), MAX_SCHEDULE_REFRESH_DELAY_MS);
};

/**
 * Checks whether browser localStorage can be used in the current runtime.
 */
const isBrowserStorageAvailable = () => typeof window !== "undefined" && window.localStorage;

/**
 * Builds the browser cache key for one bodyshop's feature flags.
 */
const getLocalStorageKey = (bodyshopId) => `${LOCAL_STORAGE_PREFIX}:${bodyshopId}`;

/**
 * Reads a bodyshop-scoped last-known-good flag payload from browser storage.
 */
const readCachedFeatureFlags = (bodyshopId, now = Date.now()) => {
  if (!bodyshopId || !isBrowserStorageAvailable()) return null;

  try {
    const rawValue = window.localStorage.getItem(getLocalStorageKey(bodyshopId));
    if (!rawValue) return null;

    const parsed = JSON.parse(rawValue);
    if (!parsed?.flags || typeof parsed.flags !== "object" || Array.isArray(parsed.flags)) return null;
    const cachedAt = Date.parse(parsed.cachedAt);
    if (!parsed.cachedAt || Number.isNaN(cachedAt) || now - cachedAt > LOCAL_STORAGE_MAX_AGE_MS) return null;

    return parsed.flags;
  } catch {
    return null;
  }
};

/**
 * Persists a successful backend flag payload for short-term browser fallback.
 */
const writeCachedFeatureFlags = (bodyshopId, flags) => {
  if (!bodyshopId || !flags || !isBrowserStorageAvailable()) return;

  try {
    window.localStorage.setItem(
      getLocalStorageKey(bodyshopId),
      JSON.stringify({
        cachedAt: new Date().toISOString(),
        flags
      })
    );
  } catch {
    // localStorage may be unavailable, full, or blocked. Runtime flags still work without the browser cache.
  }
};

/**
 * Builds the local client object that mimics the Split client surface used by the app.
 */
const createFeatureFlagClient = ({ bodyshop, key, backendFlags }) => {
  const attributes = {};

  const getTreatmentWithConfig = (name) => normalizeFlagValue(backendFlags?.[name]);

  return {
    client: null,
    isReady: true,
    isReadyFromCache: true,
    key: key || bodyshop?.imexshopid || "anon",
    getTreatment: (name) => getTreatmentWithConfig(name).treatment,
    getTreatmentWithConfig,
    getTreatments: (names = []) =>
      names.reduce((acc, name) => {
        acc[name] = getTreatmentWithConfig(name).treatment;
        return acc;
      }, {}),
    getTreatmentsWithConfig: (names = []) =>
      names.reduce((acc, name) => {
        acc[name] = getTreatmentWithConfig(name);
        return acc;
      }, {}),
    setAttribute: (name, value) => {
      attributes[name] = value;
      return true;
    },
    setAttributes: (values = {}) => {
      Object.assign(attributes, values);
      return true;
    },
    getAttribute: (name) => attributes[name],
    getAttributes: () => ({ ...attributes }),
    ready: () => Promise.resolve(),
    on: () => {},
    off: () => {},
    destroy: () => {}
  };
};

/**
 * Provides database-backed feature flags through a Split-shaped React context.
 */
export function SplitFactoryProvider({ children, config, factory }) {
  const bodyshop = useSelector(selectBodyshop);
  const [state, setState] = useState({ flags: {}, isReady: true, source: "local" });
  const loadIdRef = useRef(0);
  const refreshTimerRef = useRef(null);

  const loadFeatureFlags = useCallback(async () => {
    const loadId = (loadIdRef.current += 1);

    if (!bodyshop?.id) {
      setState({ flags: {}, isReady: true, source: "local" });
      return;
    }

    setState((current) => ({ ...current, isReady: false }));

    try {
      const { data } = await axios.get(`/feature-flags/bodyshops/${bodyshop.id}`);
      if (loadId !== loadIdRef.current) return;
      const flags = data.flags || {};
      writeCachedFeatureFlags(bodyshop.id, flags);
      setState({
        flags,
        isReady: true,
        source: data.source || "database"
      });
    } catch (error) {
      if (loadId !== loadIdRef.current) return;
      const cachedFlags = readCachedFeatureFlags(bodyshop.id);
      console.warn("Feature flags backend fetch failed; falling back to last-known browser cache.", error);
      setState({
        flags: cachedFlags || {},
        isReady: true,
        source: cachedFlags ? "browser-cache" : "local"
      });
    }
  }, [bodyshop?.id]);

  useEffect(() => {
    loadFeatureFlags();

    return () => {
      loadIdRef.current += 1;
    };
  }, [loadFeatureFlags]);

  useEffect(() => {
    if (!bodyshop?.id) return undefined;

    const handleFeatureFlagsChanged = (event) => {
      if (!isFeatureFlagChangeRelevant(event.detail, bodyshop.id)) return;

      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }

      refreshTimerRef.current = setTimeout(() => {
        refreshTimerRef.current = null;
        loadFeatureFlags();
      }, FEATURE_FLAGS_REFRESH_DEBOUNCE_MS);
    };

    window.addEventListener(FEATURE_FLAGS_CHANGED_EVENT, handleFeatureFlagsChanged);

    return () => {
      window.removeEventListener(FEATURE_FLAGS_CHANGED_EVENT, handleFeatureFlagsChanged);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [bodyshop?.id, loadFeatureFlags]);

  useEffect(() => {
    const delay = getNextScheduleRefreshDelay(state.flags);
    if (delay == null) return undefined;

    const timer = setTimeout(() => {
      setState((current) => ({ ...current, flags: { ...current.flags } }));
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [state.flags]);

  const value = useMemo(
    () => ({ config, factory, flags: state.flags, isReady: state.isReady, source: state.source }),
    [config, factory, state.flags, state.isReady, state.source]
  );
  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

/**
 * Returns a Split-compatible client backed by the local feature flag context.
 */
export function useSplitClient(options = {}) {
  const bodyshop = useSelector(selectBodyshop);
  const context = useContext(FeatureFlagContext);

  const client = useMemo(() => {
    const nextClient = createFeatureFlagClient({
      bodyshop,
      key: options.key,
      backendFlags: context.flags
    });
    nextClient.client = nextClient;
    nextClient.isReady = context.isReady;
    nextClient.isReadyFromCache = context.source === "redis" || context.source === "browser-cache";
    return nextClient;
  }, [bodyshop, options.key, context.flags, context.isReady, context.source]);

  return client;
}

/**
 * Returns treatment/config pairs for several feature flags.
 */
export function useTreatmentsWithConfig({ names = [] } = {}) {
  const client = useSplitClient();

  return useMemo(
    () => ({
      treatments: client.getTreatmentsWithConfig(names),
      isReady: client.isReady,
      isReadyFromCache: client.isReadyFromCache,
      lastUpdate: Date.now()
    }),
    [client, names]
  );
}

/**
 * Returns only the treatment string for one feature flag.
 */
export function useTreatment({ name } = {}) {
  const client = useSplitClient();
  return client.getTreatment(name);
}

/**
 * Returns the treatment/config pair for one feature flag.
 */
export function useTreatmentWithConfig({ name } = {}) {
  const client = useSplitClient();
  return client.getTreatmentWithConfig(name);
}

export const FeatureFlagProvider = SplitFactoryProvider;
export const useFeatureFlagClient = useSplitClient;
export const SplitContext = FeatureFlagContext;
export const useSplitContext = () => useContext(FeatureFlagContext);

export const __featureFlagTesting = {
  createFeatureFlagClient,
  getNextScheduleRefreshDelay,
  getLocalStorageKey,
  isFeatureFlagChangeRelevant,
  normalizeFlagValue,
  readCachedFeatureFlags,
  writeCachedFeatureFlags
};
